import { useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import Modal from "src/components/modal/modal";
import type { SubscriptionProvider } from "src/types/subscription.types";
import type { StoreSubscriptionVerification as StoreSubscriptionVerificationResult } from "../subscription-users.service";
import { useVerifyStoreSubscriptionMutation } from "../subscription-users.service";

type VerifyButtonProps = {
  isDarkMode?: boolean;
  label?: string;
  isLoading?: boolean;
  onVerify: () => void;
};

type VerificationModalProps = {
  isOpen: boolean;
  provider?: StoreSubscriptionProvider;
  isLoading: boolean;
  result?: StoreSubscriptionVerificationResult;
  errorMessage?: string;
  onClose: () => void;
};

export type StoreSubscriptionProvider = Extract<
  SubscriptionProvider,
  "APPLE" | "GOOGLE"
>;

export const isStoreSubscriptionProvider = (
  provider: SubscriptionProvider | undefined,
): provider is StoreSubscriptionProvider =>
  provider === "APPLE" || provider === "GOOGLE";

export type StoreSubscriptionVerificationTarget = {
  userId: string;
  subscriptionId: string;
  provider: StoreSubscriptionProvider;
};

const formatDateTime = (value: string | null) => {
  if (!value) return "Not available";

  return new Date(value).toLocaleString();
};

const formatStoreStatus = (status: string | string[] | null) => {
  if (!status) return "Not available";

  return Array.isArray(status) ? status.join(", ") : status;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getStringValue = (value: unknown) =>
  typeof value === "string" && value.trim() ? value : undefined;

const getErrorMessage = (error: unknown) => {
  if (!isRecord(error))
    return "Unable to verify this subscription with the store.";

  const errorData = error.data;
  const dataError = isRecord(errorData) ? errorData.error : undefined;

  return (
    getStringValue(error.message) ??
    getStringValue(errorData) ??
    getStringValue(dataError) ??
    "Unable to verify this subscription with the store."
  );
};

const VerificationResult = ({
  result,
}: {
  result: StoreSubscriptionVerificationResult;
}) => (
  <div className="grid gap-4 md:grid-cols-2">
    <section className="rounded-lg border border-slate-200 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">
        Local record
      </h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Status</dt>
          <dd className="font-medium text-slate-800">{result.localStatus}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">End date</dt>
          <dd className="text-right font-medium text-slate-800">
            {formatDateTime(result.localEndDate)}
          </dd>
        </div>
      </dl>
    </section>

    <section className="rounded-lg border border-sky-200 bg-sky-50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-sky-900">
        {result.provider} store
      </h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-sky-700">Status</dt>
          <dd className="text-right font-medium text-sky-900">
            {formatStoreStatus(result.store.status)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-sky-700">End date</dt>
          <dd className="text-right font-medium text-sky-900">
            {formatDateTime(result.store.endDate)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-sky-700">Auto-renew</dt>
          <dd className="font-medium text-sky-900">
            {result.store.autoRenewEnabled === null
              ? "Not available"
              : result.store.autoRenewEnabled
                ? "On"
                : "Off"}
          </dd>
        </div>
      </dl>
    </section>
  </div>
);

export const VerifyStoreSubscriptionButton = ({
  isDarkMode = false,
  label = "Verify with store",
  isLoading = false,
  onVerify,
}: VerifyButtonProps) => {
  const buttonClassName = isDarkMode
    ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
    : "border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50";

  return (
    <button
      type="button"
      onClick={onVerify}
      disabled={isLoading}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
    >
      <FaSyncAlt className={isLoading ? "animate-spin" : ""} />
      {label}
    </button>
  );
};

export const StoreSubscriptionVerificationModal = ({
  isOpen,
  provider,
  isLoading,
  result,
  errorMessage,
  onClose,
}: VerificationModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size={2}>
    <div className="pr-8">
      <p className="text-sm font-medium text-sky-700">
        Store subscription verification
      </p>
      <h2 className="mt-1 text-xl font-semibold text-slate-900">
        {provider} subscription
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        This check is read-only and does not change the subscription or user
        record.
      </p>
    </div>

    <div className="mt-6">
      {isLoading && (
        <div className="text-sm text-slate-600">
          Checking the current store state...
        </div>
      )}
      {errorMessage && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {errorMessage}
        </div>
      )}
      {result && <VerificationResult result={result} />}
    </div>
  </Modal>
);

export const useStoreSubscriptionVerification = () => {
  const [target, setTarget] = useState<StoreSubscriptionVerificationTarget>();
  const [result, setResult] = useState<StoreSubscriptionVerificationResult>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [verifyStoreSubscription, { isLoading }] =
    useVerifyStoreSubscriptionMutation();

  const verify = async ({
    userId,
    subscriptionId,
    provider,
  }: StoreSubscriptionVerificationTarget) => {
    setTarget({ userId, subscriptionId, provider });
    setResult(undefined);
    setErrorMessage(undefined);

    try {
      const verification = await verifyStoreSubscription({
        userId,
        subscriptionId,
      }).unwrap();
      setResult(verification);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return {
    verify,
    isLoading,
    modalProps: {
      isOpen: Boolean(target),
      provider: target?.provider,
      isLoading,
      result,
      errorMessage,
      onClose: () => setTarget(undefined),
    },
  };
};
